<template>
  <div>
    <ClinicPageHeader
      title="Financeiro da Clínica"
      subtitle="Operação financeira — faturamento, recebimentos e repasses aos profissionais de saúde"
    >
      <template #actions>
        <select
          v-model="month"
          class="select"
          data-test="month"
          style="width:180px;height:40px"
          @change="onMonthChange"
        >
          <option v-for="m in months" :key="m.value" :value="m.value">
            {{ m.label }}
          </option>
        </select>
      </template>
    </ClinicPageHeader>

    <div class="dbody">
      <div class="tabs" style="margin-bottom:var(--space-6)">
        <router-link
          class="tab"
          active-class="tab--active"
          exact-path
          :to="{ name: 'ClinicFinanceDashboard', query: monthQuery }"
        >
          Visão geral
        </router-link>
        <router-link
          class="tab"
          active-class="tab--active"
          exact-path
          :to="{ name: 'ClinicFinanceReceivables', query: monthQuery }"
        >
          Recebimentos
        </router-link>
        <router-link
          class="tab"
          active-class="tab--active"
          exact-path
          :to="{ name: 'ClinicFinanceTransfers', query: monthQuery }"
        >
          Repasses
        </router-link>
        <router-link
          class="tab"
          active-class="tab--active"
          exact-path
          :to="{ name: 'ClinicFinanceStatement', query: monthQuery }"
        >
          Extrato
        </router-link>
      </div>

      <!-- Erro -->
      <ClinicStateBox
        v-if="state === 'error'"
        state="error"
        title="Não foi possível carregar o financeiro"
        message="Ocorreu um erro ao buscar os indicadores financeiros da clínica. Tente novamente."
      >
        <template #actions>
          <button class="btn btn--primary btn--sm" @click="fetch">
            Tentar de novo
          </button>
        </template>
      </ClinicStateBox>

      <!-- Loading -->
      <!-- 403: sem acesso ao Financeiro nesta clínica (US-C11) -->
      <ClinicStateBox
        v-else-if="state === 'forbidden'"
        state="empty"
        title="Acesso restrito"
        message="Você não tem acesso ao Financeiro desta clínica. Fale com o administrador."
      />

      <template v-else-if="state === 'loading'">
        <div class="metrics4" style="margin-bottom:var(--space-4)">
          <div v-for="i in 4" :key="i" class="metric">
            <div
              class="skel-line"
              style="width:36px;height:36px;border-radius:8px;margin-bottom:10px"
            />
            <div class="skel-line" style="width:70%;margin-bottom:8px" />
            <div class="skel-line" style="width:45%;height:24px" />
          </div>
        </div>
        <div class="card">
          <div class="skel-line" style="width:40%;margin-bottom:20px" />
          <div class="skel-line" style="width:100%;height:120px" />
        </div>
      </template>

      <!-- Conteúdo -->
      <template v-else>
        <div class="metrics4" style="margin-bottom:var(--space-4)">
          <ClinicKpi
            label="Faturamento do mês"
            :value="kpiMoney(revenueCents)"
            value-class="money--pos"
            icon-color="success"
            :icon="icons.money"
          />
          <ClinicKpi
            label="Consultas realizadas"
            :value="String(consultasRealizadas)"
            icon-color="primary"
            :icon="icons.check"
            foot="faturadas no mês"
          />
          <ClinicKpi
            label="Ticket médio"
            :value="kpiMoney(ticketCents)"
            icon-color="accent"
            :icon="icons.chart"
          />
          <router-link
            :to="{ name: 'ClinicFinanceTransfers', query: monthQuery }"
            class="metric card--interactive"
            style="text-decoration:none;border-color:var(--color-warning)"
          >
            <div
              class="metric-icon"
              style="background:var(--color-warning-light);color:var(--color-warning)"
              v-html="icons.clock"
            />
            <span class="metric-label">Repasses pendentes</span>
            <span class="metric-value sm">
              {{ kpiMoney(pendingTransferCents) }}
            </span>
            <span
              style="font-size:var(--font-size-xs);color:var(--color-accent);font-weight:var(--fw-bold)"
            >
              {{ pendingTransferCount }} profissionais de saúde →
            </span>
          </router-link>
        </div>

        <div
          style="display:grid;grid-template-columns:1.55fr 1fr;gap:var(--space-4)"
        >
          <div class="card">
            <div class="card-head">
              <h3>Evolução · faturamento</h3>
              <span class="badge badge--neutral badge--no-dot">
                últimos 6 meses
              </span>
            </div>
            <div class="bars">
              <div
                v-for="b in evolution"
                :key="b.label"
                class="bar"
                :class="{ on: b.on }"
              >
                <i :style="{ height: b.value + '%' }" />
                <span>{{ b.label }}</span>
              </div>
            </div>
            <hr
              style="border:none;border-top:1px solid var(--color-gray-100);margin:var(--space-5) 0"
            />
            <span class="fieldset-title">Composição do mês</span>
            <div class="breakdown" style="margin-top:var(--space-2)">
              <div
                v-for="row in breakdown"
                :key="row.label"
                class="br"
                :class="{ 'br--total': row.total }"
              >
                {{ row.label }}
                <b class="money" :class="row.moneyClass" :style="row.style">
                  {{ row.prefix }}{{ formatMoney(row.cents) }}
                </b>
              </div>
            </div>
          </div>

          <div class="card card--flush">
            <div class="card-head flush">
              <h3>Recebimentos recentes</h3>
              <router-link
                :to="{ name: 'ClinicFinanceReceivables', query: monthQuery }"
                class="seelink"
              >
                Ver todos →
              </router-link>
            </div>
            <table class="table">
              <tbody>
                <tr
                  v-for="r in recentReceipts"
                  :key="r.id"
                  class="tr--link"
                  @click="goReceivables"
                >
                  <td>
                    <div class="stack" style="gap:1px">
                      <span class="cell-strong">{{ r.patient }}</span>
                      <span
                        style="font-size:var(--font-size-xs);color:var(--color-gray-400)"
                      >
                        {{ r.meta }}
                      </span>
                    </div>
                  </td>
                  <td class="num t-right">
                    <div class="stack" style="gap:3px;align-items:flex-end">
                      <span class="money">{{ formatMoney(r.cents) }}</span>
                      <span class="badge" :class="`badge--${r.statusVariant}`">
                        {{ r.status }}
                      </span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <div
              style="padding:var(--space-4) var(--space-5);border-top:1px solid var(--color-gray-100)"
            >
              <router-link
                :to="{ name: 'ClinicFinanceTransfers', query: monthQuery }"
                class="btn btn--outline btn--sm btn--block"
              >
                Gerenciar repasses aos profissionais de saúde
              </router-link>
            </div>
          </div>
        </div>

        <p
          style="font-size:var(--font-size-xs);color:var(--color-gray-400);margin-top:var(--space-6);max-width:760px"
        >
          Financeiro da operação da clínica — distinto do Financeiro do
          Profissional de saúde. Sem TISS, contabilidade, DRE ou conciliação
          bancária. Apresenta faturamento por consulta paga, repasses devidos
          aos profissionais de saúde e margem da clínica.
        </p>
      </template>
    </div>
  </div>
</template>

<script>
import ClinicPageHeader from "@/components/clinica/ClinicPageHeader.vue";
import ClinicKpi from "@/components/clinica/ClinicKpi.vue";
import ClinicStateBox from "@/components/clinica/ClinicStateBox.vue";
import { ClinicFinance } from "@/services/clinica";
import { formatMoney } from "@/data/clinica";
import clinicFinanceMonth from "@/mixins/clinicFinanceMonth";

const ICONS = {
  money: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2v20M17 6H9.5a3.5 3.5 0 100 7h5a3.5 3.5 0 110 7H6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
  check: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  chart: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M3 17l5-5 4 3 7-8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M16 4h4v4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  clock: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/><path d="M12 7v5l3 2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`
};

// Recebimento: status do backend → rótulo + variante do badge.
const RECEIPT_STATUS = {
  recebido: { label: "Recebido", variant: "success" },
  pendente: { label: "Pendente", variant: "warning" },
  estornado: { label: "Estornado", variant: "danger" }
};

export default {
  name: "ClinicFinanceDashboard",
  components: { ClinicPageHeader, ClinicKpi, ClinicStateBox },
  mixins: [clinicFinanceMonth],
  data() {
    return {
      // loading → ready/error (ver ClinicDashboard). Preview de erro via ?state=error.
      state: "loading",
      icons: ICONS,
      // Composição financeira (centavos) vinda de ClinicFinance.summary().
      summary: null,
      evolution: [],
      recentReceipts: []
    };
  },
  created() {
    this.fetch();
  },
  methods: {
    formatMoney,
    // Formatação curta (sem centavos) para os headlines de KPI, como no protótipo.
    kpiMoney(cents) {
      // minimumFractionDigits:0 é obrigatório: sem ele, o padrão do BRL (2) fica
      // maior que maximumFractionDigits:0 e o Intl.NumberFormat lança RangeError,
      // que o Vue engolia no render e travava a tela no skeleton de loading.
      return ((cents || 0) / 100).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    },
    onMonthChanged() {
      this.fetch();
    },
    async fetch() {
      this.state = "loading";
      try {
        const fail = this.$route.query.state === "error";
        const [sumRes, recRes] = await Promise.all([
          ClinicFinance.summary({ month: this.month, fail }),
          ClinicFinance.receivables({ month: this.month, fail })
        ]);
        this.summary = sumRes.data;
        this.evolution = sumRes.data.evolution || [];
        this.recentReceipts = (recRes.data || []).slice(0, 4).map(r => {
          const s = RECEIPT_STATUS[r.status] || {
            label: r.status,
            variant: "neutral"
          };
          return {
            id: r.id,
            patient: r.patientName,
            meta: `${this.shortDate(r.date)} · ${r.method}`,
            cents: r.grossCents,
            status: s.label,
            statusVariant: s.variant
          };
        });
        this.state = "ready";
      } catch (e) {
        // 403 = RBAC (papel Atendimento) — não é falha de carregamento
        this.state =
          e && e.response && e.response.status === 403 ? "forbidden" : "error";
      }
    },
    shortDate(iso) {
      const [, m, d] = String(iso).split("-");
      return `${d}/${m}`;
    },
    goReceivables() {
      this.$router.push({ name: "ClinicFinanceReceivables" });
    }
  },
  computed: {
    revenueCents() {
      return this.summary ? this.summary.billingCents : 0;
    },
    ticketCents() {
      return this.summary ? this.summary.ticketAvgCents : 0;
    },
    pendingTransferCents() {
      return this.summary ? this.summary.pendingTransfersCents : 0;
    },
    pendingTransferCount() {
      return this.summary ? this.summary.pendingTransfersCount : 0;
    },
    // Nº de consultas faturadas derivado do faturamento ÷ ticket médio.
    consultasRealizadas() {
      if (!this.summary || !this.summary.ticketAvgCents) return 0;
      return Math.round(
        this.summary.billingCents / this.summary.ticketAvgCents
      );
    },
    // Composição reativa: faturamento ▶ repasses ▶ taxa gateway ▶ margem.
    breakdown() {
      const s = this.summary;
      if (!s) return [];
      return [
        {
          label: `Faturamento bruto (${this.consultasRealizadas} consultas)`,
          cents: s.billingCents
        },
        {
          label: "Repasses aos profissionais de saúde (70%)",
          cents: s.transfersCents,
          prefix: "− ",
          style: "color:var(--color-danger)"
        },
        {
          label: "Taxa de pagamento (gateway)",
          cents: s.gatewayFeeCents,
          prefix: "− ",
          style: "color:var(--color-danger)"
        },
        {
          label: "Margem da clínica",
          cents: s.marginCents,
          moneyClass: "money--pos",
          total: true
        }
      ];
    }
  }
};
</script>
